/**
 * Keeps the PDP rewards USP amount in step with the selected variant.
 *
 * Liquid renders the first amount (blocks/rewards-usp.liquid). x-product-selector broadcasts
 * `variant-selected` without a price, so this reads the formatted price it writes to the form's
 * `variant_price` input, the same source the CTA price uses.
 */

const VARIANT_EVENT = 'variant-selected'
const PRICE_INPUT_SELECTOR = 'input[name="variant_price"]'
const AMOUNT_SELECTOR = '[data-role="rewards-usp-amount"]'

/** "$1,299.95" -> 129995 cents. Returns null when the input holds no price yet. */
const parseMoneyToCents = (formatted: string): number | null => {
  const numeric = parseFloat(formatted.replace(/[^0-9.]/g, ''))
  return Number.isFinite(numeric) ? Math.round(numeric * 100) : null
}

const formatMoney = (cents: number): string => {
  const currency = window.Shopify?.currency?.active || 'AUD'
  // Round to whole cents first so 599.5 lands on $6.00, as Liquid's `money` does server-side.
  return new Intl.NumberFormat('en-AU', { style: 'currency', currency }).format(Math.round(cents) / 100)
}

class RewardsUSP extends HTMLElement {
  private handleVariantSelected = () => {
    const input = document.querySelector<HTMLInputElement>(PRICE_INPUT_SELECTOR)
    const priceCents = input ? parseMoneyToCents(input.value) : null
    if (priceCents === null) return

    const percentage = Number(this.dataset.percentage) || 10
    const target = this.querySelector(AMOUNT_SELECTOR)
    if (target) target.textContent = formatMoney(priceCents * (percentage / 100))
  }

  connectedCallback() {
    window.addEventListener(VARIANT_EVENT, this.handleVariantSelected)
  }

  disconnectedCallback() {
    window.removeEventListener(VARIANT_EVENT, this.handleVariantSelected)
  }
}

declare global {
  interface Window {
    Shopify?: { currency?: { active?: string } }
  }
}

if (!customElements.get('rewards-usp')) {
  customElements.define('rewards-usp', RewardsUSP)
}
